import { useCurrentProject, WithPermission } from 'src/providers/current-project.provider'
import { PatientListProvider, usePatientList } from './_components/patients.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { CurecallPaginationBar } from '@honestica/curecall-ui'
import PatientListHeader from './_components/patients.header'
import PatientListTable from './_components/patients.table'
import { useTranslation } from 'react-i18next'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { useSearchParams } from 'react-router-dom'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { TPatientListColumnName } from 'src/types/patient-list'
import { EList, getListPreferedOrder, TOrderBy } from 'src/utils/list-order-preference.util'
import { useEffect } from 'react'

export default function PatientsPage() {
  const { project, isFeatureActivated } = useCurrentProject()
  const [searchParams] = useSearchParams()
  const navigate = useLocalizedNavigate()

  const defaultOrderBy: TOrderBy<TPatientListColumnName> = getListPreferedOrder(EList.PATIENT_LIST) || {
    column:
      (searchParams.get('column') as TPatientListColumnName | undefined) ??
      project.PatientListTabs?.[0]?.PatientListTabColumns[0]?.PatientListColumn.name,
    descOrder: searchParams.get('descOrder') === 'true',
  }

  const shouldSeePlanning = isFeatureActivated('is-planning-patient-activated')
  useEffect(() => {
    if (shouldSeePlanning) {
      navigate(`/${project.id}/medical/planning`, { replace: true })
    }
  }, [shouldSeePlanning, project, navigate])

  if (shouldSeePlanning) return <div className="loading" />

  return (
    <WithPermission
      requiredSubject={EPermissionGrantSubjects.PATIENT}
      requiredAction={EPermissionGrantActions.GET}
      onFailure="permission-error-component"
    >
      <PatientListProvider defaultOrderBy={defaultOrderBy}>
        <PatientListPage />
      </PatientListProvider>
    </WithPermission>
  )
}

const PatientListPage = () => {
  const { project } = useCurrentProject()
  const { t } = useTranslation()

  const { isPatientListSpoiled, setRequestProps, currentOrder, currentTab, isLoading, error, data } =
    usePatientList()

  const handleSelectTab = (pageNumber: number) => {
    if (data && currentTab) {
      setRequestProps({
        tabId: currentTab.id,
        body: { pageNumber, orderBy: currentOrder, patientsPerPage: data.patientsPerPage },
      })
    }
  }

  if (!project) return
  if (error && error !== 'no patient list tabs') return <ErrorQueryHandler error={error} />

  return (
    <div className="c-flex c-flex-col">
      <PatientListHeader
        error={error}
        patientListTabs={project.PatientListTabs}
        currentTab={currentTab}
        currentOrder={currentOrder}
        onSelectTab={setRequestProps}
        isPatientListSpoiled={isPatientListSpoiled}
      />
      {!error && (
        <>
          <PatientListTable
            patientsPerPage={data?.patientsPerPage}
            onTableHeaderClick={setRequestProps}
            currentPage={data?.currentPage}
            currentOrder={currentOrder}
            data={data}
            currentTab={currentTab}
            isLoading={isLoading}
            projectId={project.id}
          />
          <CurecallPaginationBar
            pageLabel={t('common.actions.page')}
            itemsPerPage={data?.patientsPerPage}
            totalItems={data?.totalPatientCount ?? 0}
            currentPage={data?.currentPage}
            pagesLabel={t('common.actions.pages')}
            numberOfVisiblePages={7}
            onClick={handleSelectTab}
            additionalNode={
              <div className="c-min-w-fit c-w-max c-font-medium c-text-grey-500">
                {t('pages.patient-list-page.patients-count', { totalPatientCount: data?.totalPatientCount })}
              </div>
            }
          />
        </>
      )}
    </div>
  )
}
